import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, Dimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../contexts/ThemeContext';
import Footer from '../components/Footer';
import { formatCurrency, formatNumber } from '../utils/formatters';
import { LineChart } from 'react-native-chart-kit';

const NSCCalculatorScreen = () => {
  const { colors } = useTheme();
  const navigation = useNavigation();
  const [screenData, setScreenData] = useState(Dimensions.get('window'));
  const isMobile = screenData.width < 900;

  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenData(window);
    });
    return () => subscription?.remove();
  }, []);

  const [results, setResults] = useState(null);
  const [showFAQ, setShowFAQ] = useState(true);

  const [formData, setFormData] = useState({
    investment: '100000',
    interestRate: '7.7',
    years: '5',
  });

  const handleInputChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const calculateNSC = () => {
    const P = parseFloat(formData.investment);
    const r = parseFloat(formData.interestRate) / 100;
    const t = parseFloat(formData.years);
    if ([P, r, t].some(v => isNaN(v))) { setResults(null); return; }
    let balance = P;
    const yearlySeries = [];
    for (let y = 1; y <= t; y++) {
      balance = balance * (1 + r);
      yearlySeries.push(balance);
    }
    const totalValue = balance;
    const expectedReturns = totalValue - P;
    setResults({ investedAmount: Math.round(P), expectedReturns: Math.round(expectedReturns), totalValue: Math.round(totalValue), yearlySeries, years: t });
  };

  useEffect(() => { calculateNSC(); }, [formData]);

  const renderHeader = () => (
    <View style={styles.heroSection}>
      <View style={styles.heroContent}>
        <Text style={styles.heroTitle}>NSC Calculator</Text>
        <Text style={styles.heroSubtitle}>Estimate maturity value for National Savings Certificate (NSC)</Text>
      </View>
    </View>
  );

  const renderCalculator = () => (
    <View style={styles.calculatorContainer}>
      <View style={[styles.calculatorRow, isMobile && { flexDirection: 'column' }]}>
        <View style={[styles.formContent, isMobile && { flex: 1 }]}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Investment Amount</Text>
            <View style={styles.inputContainer}>
              <Text style={styles.currencySymbol}>₹</Text>
              <TextInput style={styles.input} value={formData.investment} onChangeText={(v)=>handleInputChange('investment', v)} keyboardType="numeric" placeholder="100000" />
            </View>
          </View>
          <View style={[styles.inputRow, isMobile && { flexDirection: 'column', gap: 0 }]}>
            <View style={[styles.inputGroup, styles.half, isMobile && { marginBottom: 16 }]}>
              <Text style={styles.label}>Interest Rate (p.a.)</Text>
              <View style={styles.inputContainer}>
                <TextInput style={styles.input} value={formData.interestRate} onChangeText={(v)=>handleInputChange('interestRate', v)} keyboardType="numeric" placeholder="7.7" />
                <Text style={styles.percentSymbol}>%</Text>
              </View>
              <Text style={styles.helperText}>Typically fixed by government; default tenure is 5 years</Text>
            </View>
            <View style={[styles.inputGroup, styles.half, isMobile && { marginBottom: 16 }]}>
              <Text style={styles.label}>Tenure</Text>
              <View style={styles.inputContainer}>
                <TextInput style={styles.input} value={formData.years} onChangeText={(v)=>handleInputChange('years', v)} keyboardType="numeric" placeholder="5" />
                <Text style={styles.suffixText}>Years</Text>
              </View>
            </View>
          </View>
        </View>
        <View style={[styles.summaryPanel, isMobile && { flex: 1, marginTop: 16 }]}>
          <View style={styles.panelHeader}>
            <Text style={styles.panelTitle}>Investment Summary</Text>
            <Text style={styles.panelSubtitle}>NSC Investment Results</Text>
          </View>
          {results ? (
            <View style={styles.resultsContainer}>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Expected Returns</Text><Text style={styles.summaryValue}>{formatCurrency(results.expectedReturns)}</Text></View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Invested Amount</Text><Text style={styles.summaryValue}>{formatCurrency(results.investedAmount)}</Text></View>
              <View style={styles.totalRow}><Text style={styles.totalLabel}>Total Value</Text><Text style={styles.totalValue}>{formatCurrency(results.totalValue)}</Text></View>
              {renderChart()}
            </View>
          ) : (
            <Text style={styles.placeholderText}>Enter valid inputs to view results</Text>
          )}
        </View>
      </View>
    </View>
  );

  const renderChart = () => {
    if (!results?.yearlySeries?.length) return null;
    const labels = results.yearlySeries.map((_, i) => {
      const y = i + 1; return (y === 1 || y === results.years) ? String(y) : '';
    });
    const chartWidth = isMobile ? Math.min(screenData.width - 40, 320) : 320;
    return (
      <View style={styles.chartSection}>
        <Text style={styles.chartTitle}>Yearly Balance Projection</Text>
        <LineChart
          data={{ labels, datasets: [{ data: results.yearlySeries.map(v=>Math.round(v)) }] }}
          width={chartWidth}
          height={180}
          yAxisLabel="₹"
          chartConfig={{ backgroundColor: '#ffffff', backgroundGradientFrom: '#ffffff', backgroundGradientTo: '#ffffff', decimalPlaces: 0, color: ()=>'#2563EB', labelColor: ()=>'#0f172a' }}
          style={{ marginTop: 8 }}
        />
      </View>
    );
  };

  const renderInfo = () => (
    <View style={styles.infoSection}>
      <Text style={styles.sectionTitle}>About NSC</Text>
      <Text style={styles.sectionText}>National Savings Certificate offers guaranteed returns with annual compounding. This calculator projects maturity value for your investment.</Text>
    </View>
  );

  const renderFAQ = () => (
    <View style={styles.faqContainer}>
      <TouchableOpacity style={styles.faqHeader} onPress={() => setShowFAQ(!showFAQ)}>
        <Text style={styles.faqTitle}>Frequently Asked Questions</Text>
        <Ionicons name={showFAQ ? 'chevron-up' : 'chevron-down'} size={20} color="#1f2937" />
      </TouchableOpacity>
      {showFAQ && (
        <View style={styles.faqContent}>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Is the interest rate fixed?</Text>
            <Text style={styles.faqAnswer}>Rates are notified by the government and can change for new issues; the calculator assumes a constant rate for projection.</Text>
          </View>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Is NSC interest compounded annually?</Text>
            <Text style={styles.faqAnswer}>Yes. NSC compounds annually and pays maturity proceeds at the end of tenure.</Text>
          </View>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What are the tax benefits?</Text>
            <Text style={styles.faqAnswer}>Investments may qualify for deduction under Section 80C (subject to limits). Interest is typically taxable. Consult a tax advisor for personal scenarios.</Text>
          </View>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Can I withdraw NSC prematurely?</Text>
            <Text style={styles.faqAnswer}>Premature encashment is restricted and allowed only under specific conditions (e.g., court order, pledge release). Penalties may apply.</Text>
          </View>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Who should consider NSC?</Text>
            <Text style={styles.faqAnswer}>Investors seeking guaranteed returns and low risk for medium-term goals can consider NSC. Compare with PPF and FDs for suitability.</Text>
          </View>
        </View>
      )}
    </View>
  );

  const renderSEO = () => (
    <View style={styles.seoContainer}>
      <Text style={styles.seoTitle}>NSC Calculator: Guaranteed Returns Projection</Text>
      <Text style={styles.sectionText}>National Savings Certificate (NSC) is a government-backed small savings scheme offering assured returns with annual compounding. This calculator estimates the maturity value based on your investment amount, current NSC rate and tenure.</Text>

      <Text style={styles.seoSubtitle}>Key features of NSC</Text>
      <Text style={styles.sectionText}>• Government-backed safety • Fixed returns for the tenure • Annual compounding • Eligible for Section 80C deduction (subject to limits) • Ideal for medium-term goals</Text>

      <Text style={styles.seoSubtitle}>Compare with other options</Text>
      <View style={styles.linksRow}>
        <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('FDCalculator')}>
          <Text style={styles.linkText}>FD Calculator</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('PPFCalculator')}>
          <Text style={styles.linkText}>PPF Calculator</Text>
        </TouchableOpacity>
      </View>
      <Text style={styles.disclaimer}>Returns and rules are subject to government notifications. Use this tool for planning only.</Text>
    </View>
  );

  return (
    <ScrollView style={styles.container}>
      {renderHeader()}
      {renderCalculator()}
      {renderInfo()}
      {renderFAQ()}
      {renderSEO()}
      <Footer />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f8fafc' },
  heroSection: { backgroundColor: '#2563EB', paddingVertical: 24, paddingHorizontal: 30 },
  heroContent: { width: '100%', alignSelf: 'stretch' },
  heroTitle: { fontSize: 28, fontWeight: 'bold', color: '#ffffff', marginBottom: 6 },
  heroSubtitle: { fontSize: 14, color: '#e0f2fe' },
  calculatorContainer: { width: '100%', alignSelf: 'stretch', paddingHorizontal: 30, paddingVertical: 20 },
  calculatorRow: { flexDirection: 'row', gap: 24 },
  formContent: { gap: 20, flex: 0.7 },
  summaryPanel: { flex: 0.3, backgroundColor: '#ffffff', borderRadius: 12, padding: 20, height: 'fit-content', borderWidth: 1, borderColor: '#e5e7eb' },
  panelHeader: { alignItems: 'center', marginBottom: 16, paddingBottom: 10, borderBottomWidth: 1, borderBottomColor: '#e5e7eb' },
  panelTitle: { fontSize: 18, fontWeight: '600', color: '#0f172a' },
  panelSubtitle: { fontSize: 12, color: '#64748b' },
  inputGroup: { marginBottom: 16 },
  inputRow: { flexDirection: 'row', gap: 16 },
  half: { flex: 1 },
  label: { fontSize: 16, fontWeight: '600', color: '#0f172a', marginBottom: 8 },
  helperText: { fontSize: 12, color: '#64748b', marginTop: 6 },
  inputContainer: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f8fafc', borderRadius: 8, borderWidth: 1, borderColor: '#e5e7eb', paddingHorizontal: 12, height: 48 },
  currencySymbol: { fontSize: 16, color: '#64748b', marginRight: 8 },
  percentSymbol: { fontSize: 16, color: '#64748b', marginLeft: 8 },
  suffixText: { fontSize: 14, color: '#64748b', marginLeft: 8 },
  input: { flex: 1, fontSize: 16, color: '#0f172a', borderWidth: 0 },
  resultsContainer: { gap: 12 },
  summaryRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  summaryLabel: { fontSize: 14, color: '#334155' },
  summaryValue: { fontSize: 16, fontWeight: '600', color: '#0f172a' },
  divider: { height: 1, backgroundColor: '#e5e7eb', marginVertical: 8 },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8 },
  totalLabel: { fontSize: 16, fontWeight: '600', color: '#0f172a' },
  totalValue: { fontSize: 18, fontWeight: 'bold', color: '#065f46' },
  placeholderText: { fontSize: 14, color: '#64748b' },
  infoSection: { marginVertical: 20, marginHorizontal:20, backgroundColor: '#ffffff', padding: 20, borderRadius: 12, elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4 },
  sectionTitle: { fontSize: 20, fontWeight: 'bold', color: '#0f172a', marginBottom: 12 },
  sectionText: { fontSize: 16, color: '#475569', lineHeight: 24, marginBottom: 12 },
  faqContainer: { marginBottom: 20, marginHorizontal:20 },
  faqHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 20, backgroundColor: '#2563EB10', borderRadius: 12 },
  faqTitle: { fontSize: 20, fontWeight: 'bold', color: '#0f172a' },
  faqContent: { backgroundColor: '#ffffff', padding: 20, borderRadius: 12, marginTop: 12, borderWidth: 1, borderColor: '#e5e7eb' },
  faqItem: { marginBottom: 16 },
  faqQuestion: { fontSize: 16, fontWeight: '600', color: '#0f172a', marginBottom: 6 },
  faqAnswer: { fontSize: 15, color: '#475569', lineHeight: 22 },
  chartSection: { marginTop: 12, alignItems: 'center' },
  chartTitle: { fontSize: 14, fontWeight: '600', color: '#0f172a', marginBottom: 8 },
  seoContainer: { backgroundColor: '#ffffff', padding: 20, marginHorizontal:20, borderRadius: 12, elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, marginBottom: 20 },
  seoTitle: { fontSize: 22, fontWeight: 'bold', color: '#0f172a', marginBottom: 12, textAlign: 'center' },
  linksRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  linkButton: { paddingVertical: 8, paddingHorizontal: 12, backgroundColor: '#2563EB10', borderRadius: 8, borderWidth: 1, borderColor: '#e5e7eb' },
  linkText: { color: '#2563EB', fontWeight: '600' },
  disclaimer: { fontSize: 12, color: '#64748b', marginTop: 4 },
});

export default NSCCalculatorScreen;